import { fetchAiDataPolicyRole, type AiDataPolicyRole } from '@shared/ai-data-policy/access';
import { NOT_CHOSEN } from '@shared/ai-data-policy/memory-service';
import { useAiDataPolicyStore } from '@shared/ai-data-policy/store';
import type { AiDataPolicy } from '@shared/ai-data-policy/types';

import { isDefaultModel, modelGroupOf } from '../data/models';
import { tr, uiLanguage } from '../i18n';
import { fetchModelsStrict, type ModelsResponse } from '../service/orchestrator-client';
import type {
  ChatService,
  DataCategory,
  Message,
  MessageRoute,
  ProcessingFailure,
  ProcessingStep,
  RouteModel,
  RouteReasonCode,
  SendOptions,
  SharedCategory,
  StreamChunk,
} from '../types';

import { classifyRequest, type Classification } from './classify';
import { hasCommercialModels, internalModelFor, selectedRouteModel } from './models';
import { usePolicyPromptStore, type PromptDecision, type PromptRequest } from './prompt.store';

export interface DataPolicyLayerDeps {
  policy: () => Promise<AiDataPolicy | null>;
  role: () => Promise<AiDataPolicyRole>;
  models: () => Promise<ModelsResponse>;
  prompt: (request: Omit<PromptRequest, 'id'>, signal?: AbortSignal) => Promise<PromptDecision>;
  language: () => string | undefined;
  now: () => number;
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
}

const STEP_MIN_MS = 600;
const ROUTE_STEP_MIN_MS = 1000;

export const CRM_DATA_TOOLSETS = ['crm_client_data', 'crm_analytics', 'documents'];

function serverQueuedTexts(history: Message[]): string[] {
  return history
    .filter((m) => m.status === 'queued' && m.queueState !== 'refused')
    .map((m) => m.content);
}

function carriesInvestorData(options: SendOptions): boolean {
  return options.investorData ?? Boolean(options.sessionId);
}

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (ms <= 0 || signal?.aborted) {
      resolve();
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        resolve();
      },
      { once: true },
    );
  });
}

export function dataLabel(categories: DataCategory[]): string {
  const investor = categories.includes('investor');
  const crm = categories.includes('crm');
  if (investor && crm) return tr('processing.data.both', 'Investor and CRM data');
  if (crm) return tr('processing.data.crm', 'CRM data');
  return tr('processing.data.investor', 'Investor data');
}

function reasonDetail(cls: Classification): string {
  switch (cls.reason) {
    case 'history':
      return tr('processing.reasons.history', 'Earlier messages include investor data');
    case 'file':
      return tr('processing.reasons.file', 'Your file includes investor data');
    case 'unsure':
      return tr(
        'processing.reasons.unsure',
        'Couldn’t confirm the data type · treated as investor data',
      );
    default: {
      const investor = cls.categories.includes('investor');
      const crm = cls.categories.includes('crm');
      if (investor && crm) return tr('processing.reasons.both', 'Involves investor and CRM data');
      if (crm) return tr('processing.reasons.crm', 'Involves CRM data');
      return tr('processing.reasons.investor', 'Involves investor data');
    }
  }
}

function reasonCode(
  cls: Classification,
  selected: RouteModel,
  policy: AiDataPolicy,
  internalRoute: boolean,
  forced: boolean,
): RouteReasonCode {
  if (selected.kind === 'internal') return 'R3';
  if (forced) return 'FB';
  if (!cls.involves) return 'R4';
  if (!internalRoute) return 'R5';
  if (policy.value === 'not_chosen') return 'R2';
  switch (cls.reason) {
    case 'history':
      return 'R1b';
    case 'file':
      return 'R1c';
    case 'unsure':
      return 'R1d';
    default:
      return 'R1';
  }
}

function sharedCategories(cls: Classification, hasFiles: boolean): SharedCategory[] {
  return [
    'question',
    'chat',
    ...(cls.involves ? (['investorData'] as const) : []),
    ...(hasFiles ? (['fileText'] as const) : []),
  ];
}

function unknownModel(modelId: string): RouteModel {
  const group = modelGroupOf({});
  return {
    id: modelId,
    label: tr('route.unknownModel', 'the model in this chat'),
    group,
    providerLabel: group.label,
    kind: 'commercial',
  };
}

interface StepClock {
  steps: ProcessingStep[];
  startedAt: Map<string, number>;
}

function beginStep(
  clock: StepClock,
  now: number,
  step: Omit<ProcessingStep, 'status'>,
): StreamChunk {
  const row: ProcessingStep = { ...step, status: 'active' };
  clock.steps.push(row);
  clock.startedAt.set(row.id, now);
  return { delta: '', done: false, step: { ...row } };
}

function endStep(
  clock: StepClock,
  now: number,
  id: string,
  status: 'done' | 'failed',
  patch: Partial<ProcessingStep> = {},
): StreamChunk | null {
  const row = clock.steps.find((s) => s.id === id);
  if (row?.status !== 'active') return null;
  const started = clock.startedAt.get(id) ?? now;
  Object.assign(row, patch, { status, durationMs: Math.max(0, now - started) });
  return { delta: '', done: false, step: { ...row } };
}

function blockedChunk(): StreamChunk {
  return {
    delta: tr(
      'processing.route.blocked',
      'This chat includes investor data, and the Investbanq model is unavailable right now, so nothing was sent. Try again shortly.',
    ),
    done: true,
    error: true,
  };
}

export function withDataPolicy(
  inner: ChatService,
  overrides: Partial<DataPolicyLayerDeps> = {},
): ChatService {
  const deps: DataPolicyLayerDeps = {
    policy: () => useAiDataPolicyStore.getState().load(),
    role: fetchAiDataPolicyRole,
    models: fetchModelsStrict,
    prompt: (request, signal) => usePolicyPromptStore.getState().open(request, signal),
    language: uiLanguage,
    now: Date.now,
    sleep: defaultSleep,
    ...overrides,
  };

  async function quietOptions(
    options: SendOptions,
    texts: string[],
  ): Promise<{ options: SendOptions; carries: boolean } | 'blocked'> {
    if (options.connected) return { options, carries: false };
    const [loaded, models] = await Promise.all([deps.policy(), deps.models()]);
    const policy = loaded ?? NOT_CHOSEN;
    if (policy.value === 'commercial_allowed') return { options, carries: true };
    const view = { sessionModelUnknown: options.sessionModelUnknown };
    const selected = selectedRouteModel(models, options.modelId, view);
    if (selected?.kind === 'internal') return { options, carries: true };
    const cls = classifyRequest({
      text: texts.join('\n'),
      history: options.history,
      files: options.files,
      language: deps.language(),
      carriedInvestorData: carriesInvestorData(options),
    });
    if (!cls.involves) {
      return { options: { ...options, blockedToolsets: CRM_DATA_TOOLSETS }, carries: false };
    }
    const internal = internalModelFor(models);
    if (!internal) return 'blocked';
    return {
      options: {
        ...options,
        modelId: internal.id,
        ...(options.queued ? { clientMessageId: undefined } : {}),
      },
      carries: true,
    };
  }

  async function* quietStream(
    options: SendOptions,
    texts: string[],
    run: (next: SendOptions) => AsyncIterable<StreamChunk>,
  ): AsyncIterable<StreamChunk> {
    const route = await quietOptions(options, texts);
    if (route === 'blocked') {
      yield blockedChunk();
      return;
    }
    if (route.carries) yield { delta: '', done: false, investorData: true };
    yield* run(route.options);
  }

  const service: ChatService = {
    ...inner,
    streamReply: (prompt, options) =>
      options.queued
        ? quietStream(options, [prompt, ...serverQueuedTexts(options.history)], (next) =>
            inner.streamReply(prompt, next),
          )
        : routedStream(inner, deps, prompt, options),
  };
  const answerInteraction = inner.answerInteraction?.bind(inner);
  if (answerInteraction) {
    service.answerInteraction = (answer, options) =>
      quietStream(options, [answer.text ?? '', ...serverQueuedTexts(options.history)], (next) =>
        answerInteraction(answer, next),
      );
  }
  const decideProposal = inner.decideProposal?.bind(inner);
  if (decideProposal) {
    service.decideProposal = (decision, options) =>
      quietStream(options, serverQueuedTexts(options.history), (next) =>
        decideProposal(decision, next),
      );
  }
  const drainQueue = inner.drainQueue?.bind(inner);
  if (drainQueue) {
    service.drainQueue = (options) =>
      quietStream(options, serverQueuedTexts(options.history), (next) => drainQueue(next));
  }
  return service;
}

async function* routedStream(
  inner: ChatService,
  deps: DataPolicyLayerDeps,
  prompt: string,
  options: SendOptions,
): AsyncIterable<StreamChunk> {
  if (options.connected) {
    yield* inner.streamReply(prompt, options);
    return;
  }
  const signal = options.signal;
  const startedAt = deps.now();
  let waitedMs = 0;
  const clock: StepClock = { steps: [], startedAt: new Map() };
  const forced = options.forceInternal === true;

  yield beginStep(clock, deps.now(), {
    id: 'understand',
    label: tr('processing.steps.understood', 'Understood your request'),
  });

  const [loadedPolicy, models, role] = await Promise.all([
    deps.policy(),
    deps.models(),
    deps.role(),
  ]);
  let policy = loadedPolicy ?? NOT_CHOSEN;
  const view = { sessionModelUnknown: options.sessionModelUnknown, connected: options.connected };
  const selected =
    selectedRouteModel(models, options.modelId, view) ?? unknownModel(options.modelId);
  const cls = classifyRequest({
    text: [prompt, ...serverQueuedTexts(options.history)].join('\n'),
    history: options.history,
    files: options.files,
    language: deps.language(),
    carriedInvestorData: carriesInvestorData(options),
  });
  await deps.sleep(STEP_MIN_MS - (deps.now() - startedAt), signal);
  const understood = endStep(clock, deps.now(), 'understand', 'done');
  if (understood) yield understood;

  if (
    cls.involves &&
    !forced &&
    policy.value === 'not_chosen' &&
    hasCommercialModels(models) &&
    role === 'admin'
  ) {
    const request: Omit<PromptRequest, 'id'> = {
      variant: selected.kind === 'commercial' ? 'paused' : 'running',
      investorName: cls.investorName,
      categories: cls.categories,
      selectedModel: selected,
    };
    if (request.variant === 'paused') {
      yield { delta: '', done: false, waiting: { kind: 'data_policy' } };
      const waitStart = deps.now();
      await deps.prompt(request, signal);
      waitedMs += deps.now() - waitStart;
      yield { delta: '', done: false, waiting: null };
      policy = (await deps.policy()) ?? policy;
    } else {
      void deps.prompt(request, signal);
    }
  }
  if (signal?.aborted) return;

  const internalRoute =
    forced ||
    selected.kind === 'internal' ||
    (cls.involves && policy.value !== 'commercial_allowed');
  const inHouse = internalRoute
    ? internalModelFor(models, selected.kind === 'internal' ? selected.id : undefined)
    : null;
  const data = dataLabel(cls.categories);
  const switching = cls.involves && selected.kind === 'commercial' && !forced;

  const failTurn = (failure: ProcessingFailure): StreamChunk => ({
    delta: '',
    done: true,
    error: true,
    failure,
    workedForMs: Math.max(0, deps.now() - startedAt - waitedMs),
  });

  if (internalRoute && !inHouse) {
    if (switching || forced) {
      yield beginStep(clock, deps.now(), {
        id: 'route',
        kind: 'route',
        label: tr('processing.route.switching', 'Switching to the Investbanq model'),
        detail: forced
          ? tr('processing.route.fallbackDetail', 'Answering inside Investbanq instead')
          : reasonDetail(cls),
      });
      await deps.sleep(ROUTE_STEP_MIN_MS, signal);
      const failed = endStep(clock, deps.now(), 'route', 'failed', {
        label: tr('processing.route.switchFailed', 'Couldn’t switch to the Investbanq model'),
        detail: tr('processing.route.notSent', '{{data}} wasn’t sent to any external model.', {
          data,
        }),
      });
      if (failed) yield failed;
      yield failTurn({ code: 'E6' });
      return;
    }
    yield beginStep(clock, deps.now(), {
      id: 'model',
      label: tr('processing.route.reaching', 'Reaching the Investbanq model'),
    });
    await deps.sleep(STEP_MIN_MS, signal);
    const failed = endStep(clock, deps.now(), 'model', 'failed', {
      label: tr('processing.route.reachFailed', 'Couldn’t reach the Investbanq model'),
      detail: cls.involves
        ? tr('processing.route.notSent', '{{data}} wasn’t sent to any external model.', { data })
        : tr(
            'processing.route.unavailable',
            'Unavailable right now. Select another model and retry.',
          ),
    });
    if (failed) yield failed;
    yield failTurn({ code: cls.involves ? 'E10b' : 'E10' });
    return;
  }

  const modelUsed = internalRoute && inHouse ? inHouse : selected;

  if (switching) {
    if (internalRoute) {
      yield beginStep(clock, deps.now(), {
        id: 'route',
        kind: 'route',
        label: tr('processing.route.switching', 'Switching to the Investbanq model'),
        detail: reasonDetail(cls),
      });
      await deps.sleep(ROUTE_STEP_MIN_MS, signal);
      const switched = endStep(clock, deps.now(), 'route', 'done', {
        label: tr('processing.route.switched', 'Switched to the Investbanq model'),
      });
      if (switched) yield switched;
    } else {
      yield beginStep(clock, deps.now(), {
        id: 'route',
        kind: 'route',
        label: tr('processing.route.using', 'Using {{model}} with investor data', {
          model: selected.label,
        }),
        detail: tr(
          'processing.route.sending',
          'Sending to {{provider}} · allowed by workspace policy',
          { provider: selected.providerLabel },
        ),
      });
      await deps.sleep(ROUTE_STEP_MIN_MS, signal);
      const used = endStep(clock, deps.now(), 'route', 'done', {
        label: tr('processing.route.used', 'Used {{model}} with investor data', {
          model: selected.label,
        }),
        detail: tr(
          'processing.route.shared',
          'Shared with {{provider}} · allowed by workspace policy',
          { provider: selected.providerLabel },
        ),
      });
      if (used) yield used;
    }
  } else if (forced) {
    yield beginStep(clock, deps.now(), {
      id: 'route',
      kind: 'route',
      label: tr('processing.route.switching', 'Switching to the Investbanq model'),
      detail: tr('processing.route.fallbackDetail', 'Answering inside Investbanq instead'),
    });
    await deps.sleep(ROUTE_STEP_MIN_MS, signal);
    const switched = endStep(clock, deps.now(), 'route', 'done', {
      label: tr('processing.route.switched', 'Switched to the Investbanq model'),
    });
    if (switched) yield switched;
  }
  if (signal?.aborted) return;

  const route: MessageRoute = {
    modelUsed,
    selectedModel: selected,
    reasonCode: reasonCode(cls, selected, policy, internalRoute, forced),
    categories: cls.categories,
    policy: {
      value: policy.value,
      setByName: policy.setBy?.name ?? null,
      setAt: policy.setAt,
    },
    sharedWithProvider: internalRoute ? null : selected.providerLabel,
    sharedCategories: internalRoute ? [] : sharedCategories(cls, (options.files?.length ?? 0) > 0),
    dataUsed: cls.involves ? cls.dataUsed : null,
    dataUsedKind: cls.involves ? (cls.categories.includes('investor') ? 'investor' : 'crm') : null,
  };

  const modelId = internalRoute
    ? modelUsed.id
    : isDefaultModel(options.modelId) && selected.id !== options.modelId
      ? selected.id
      : options.modelId;
  const offerInternal = !internalRoute && internalModelFor(models) !== null;
  const narrowTools = !internalRoute && policy.value !== 'commercial_allowed';

  let activeStatus: string | null = null;
  let seq = 0;
  const closeStatus = (status: 'done' | 'failed'): StreamChunk | null => {
    if (!activeStatus) return null;
    const chunk = endStep(clock, deps.now(), activeStatus, status);
    activeStatus = null;
    return chunk;
  };

  if (!narrowTools || !options.sessionId) {
    yield { delta: '', done: false, investorData: !narrowTools };
  }
  const sendOptions: SendOptions = {
    ...options,
    modelId,
    ...(narrowTools ? { blockedToolsets: CRM_DATA_TOOLSETS } : {}),
  };
  for await (const chunk of inner.streamReply(prompt, sendOptions)) {
    if (chunk.status) {
      const closed = closeStatus('done');
      if (closed) yield closed;
      activeStatus = `status-${String(++seq)}`;
      yield beginStep(clock, deps.now(), { id: activeStatus, label: chunk.status });
      continue;
    }
    if (chunk.delta || chunk.block) {
      const closed = closeStatus('done');
      if (closed) yield closed;
    }
    if (chunk.done) {
      const closed = closeStatus(chunk.error ? 'failed' : 'done');
      if (closed) yield closed;
      const workedForMs = Math.max(0, deps.now() - startedAt - waitedMs);
      yield chunk.error
        ? {
            ...chunk,
            workedForMs,
            ...(offerInternal && !chunk.answer
              ? { failure: { code: 'generic', offerInternalFallback: true } }
              : {}),
          }
        : { ...chunk, route, workedForMs };
      return;
    }
    yield chunk;
  }
  const closed = closeStatus('failed');
  if (closed) yield closed;
}
